'use client';
import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useDebounce } from '@/hooks/useDebounce';

export const useUrlSearchSync = (delay = 400) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const initialSearch = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const debouncedSearchQuery = useDebounce(searchQuery, delay);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleSearchSubmit = () => {
    const current = new URLSearchParams(searchParams.toString());
    if (searchQuery) {
      current.set('q', searchQuery);
    } else {
      current.delete('q');
    }

    const queryString = current.toString();
    const url = queryString ? `${pathname}?${queryString}` : pathname;

    if (searchParams.get('q') !== (searchQuery || null)) {
      router.replace(url, { scroll: false });
    }
  };

  // Sync debounced search to URL
  useEffect(() => {
    const currentQ = searchParams.get('q') || '';
    if (currentQ === debouncedSearchQuery) return;

    const current = new URLSearchParams(searchParams.toString());
    if (debouncedSearchQuery) {
      current.set('q', debouncedSearchQuery);
    } else {
      current.delete('q');
    }

    const queryString = current.toString();
    const url = queryString ? `${pathname}?${queryString}` : pathname;

    // Check avoids redundant history pushing if URL already has query state
    if (window.location.search !== `?${queryString}` && (window.location.search || queryString)) {
      router.replace(url, { scroll: false });
    }
  }, [debouncedSearchQuery, pathname, router, searchParams]);

  return {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    handleSearchChange,
    handleSearchSubmit,
  };
};
